/**
 * Academy no painel (spec 011, secao 3, e spec 035): o conteudo (curso, modulo, aula: criar, editar, publicar, ordenar
 * e mudar de modulo), a aula nascida da biblioteca do Cloudflare Stream, e as duvidas dos clientes, que viram conversa
 * e saem do sino na primeira resposta (spec 010).
 */
import { Abas, Aviso, Botao, CabecalhoDePagina, Campo, CampoCaixa, CampoSelecao, CampoTextoLongo, Carregando, Pilha, Secao, Selo, Tabela, Vazio, formatarDataHora } from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { erroDe, useApi, usePapel } from "../api";
import { DialogoDeAcao } from "./DialogoDeAcao";
import { usePlanos } from "./Matriculas";

type Aula = { id: string; slug: string; titulo: string; descricao: string | null; ordem: number; publicada: boolean; video_id: string | null; duracao_segundos: number | null; texto: string | null; assistida_por: number; concluida_por: number; marcada_por: number; perguntas_abertas: number };
type Modulo = { id: string; slug: string; titulo: string; descricao: string | null; ordem: number; publicado: boolean; planos: string[]; aulas: Aula[] };
type Curso = { id: string; slug: string; titulo: string; descricao: string | null; ordem: number; publicado: boolean; modulos: Modulo[] };
type Pergunta = { id: string; pessoa: { id: string; nome: string }; aula: { id: string; titulo: string; curso: string; modulo: string }; segundo: number; texto: string; respostas: { id: string; texto: string; autor: string; criada_em: string }[]; respondida_em: string | null; resolvida_em: string | null; resolvida_por: "cliente" | "administrador" | null; criada_em: string };
type VideoDaBiblioteca = { video_id: string; titulo: string; duracao_segundos: number | null; pronto: boolean; protegido: boolean; criado_em: string | null; aula: { id: string; titulo: string; modulo: string } | null };
type Edicao = { tipo: "curso" | "modulo" | "aula"; id: string; titulo: string; ordem: string; planos: string[]; video: string; duracao: string; texto: string; modulo: string };

const relogio = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
const semAcento = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "");
const slugDe = (titulo: string) => semAcento(titulo).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);

export function PaginaAcademy() {
  const [aba, setAba] = useState<"conteudo" | "perguntas">("conteudo");
  const { ler } = useApi();
  const perguntas = useQuery({ queryKey: ["academy-perguntas", "abertas"], queryFn: () => ler<{ itens: Pergunta[] }>("/v1/admin/academy/perguntas?estado=abertas") });
  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina titulo="Academy" descricao="Os cursos que o cliente vê na área e as dúvidas que ele manda nas aulas. O vídeo sobe no Cloudflare Stream e vira aula aqui, pela biblioteca." />
      <Abas rotulo="Academy" valor={aba} aoMudar={setAba} abas={[{ valor: "conteudo", rotulo: "Conteúdo" }, { valor: "perguntas", rotulo: "Perguntas", contagem: perguntas.data?.itens.length }]} />
      {aba === "conteudo" ? <Conteudo /> : <Perguntas />}
    </Pilha>
  );
}

// ---------------------------------------------------------------- conteudo

function Conteudo() {
  const { ler, postar, por } = useApi();
  const { pode } = usePapel();
  const qc = useQueryClient();
  const planos = usePlanos();
  const q = useQuery({ queryKey: ["academy-catalogo"], queryFn: () => ler<{ cursos: Curso[] }>("/v1/admin/academy") });
  const [novo, setNovo] = useState<"curso" | { modulo: string } | { aula: string } | null>(null);
  const [editando, setEditando] = useState<Edicao | null>(null);
  const [biblioteca, setBiblioteca] = useState<{ id: string; titulo: string } | null>(null);
  const [excluindo, setExcluindo] = useState<AlvoDaExclusao | null>(null);
  // As aulas de cada modulo paginam sozinhas (spec 058): uma pagina por modulo, so na tela.
  const [paginaDasAulas, setPaginaDasAulas] = useState<Record<string, number>>({});
  const [feito, setFeito] = useState<string>();
  const [erro, setErro] = useState<string>();
  const admin = pode("ADMIN");
  const recarregar = () => qc.invalidateQueries({ queryKey: ["academy-catalogo"] });
  const publicar = async (tipo: "cursos" | "modulos" | "aulas", id: string, valor: boolean) => {
    setErro(undefined);
    try {
      await por(`/v1/admin/academy/${tipo}/${id}`, tipo === "aulas" ? { publicada: valor } : { publicado: valor });
      await recarregar();
      setFeito(valor ? "Publicado: o cliente já vê." : "Despublicado: sai da área do cliente.");
    } catch (e) {
      setErro(erroDe(e));
    }
  };
  if (q.isLoading) return <Carregando compacto texto="Buscando o conteúdo" />;
  if (q.isError) return <Aviso tipo="erro">{erroDe(q.error)}</Aviso>;
  const cursos = q.data?.cursos ?? [];
  const nomeDoPlano = (slug: string) => planos.data?.itens.find((p) => p.slug === slug)?.nome ?? slug;
  const modulos = cursos.flatMap((c) => c.modulos.map((m) => ({ valor: m.id, rotulo: cursos.length > 1 ? `${c.titulo} · ${m.titulo}` : m.titulo })));
  const vazia = { planos: [] as string[], video: "", duracao: "", texto: "", modulo: "" };
  return (
    <Pilha espaco={5}>
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {admin && <div><Botao onClick={() => { setFeito(undefined); setNovo("curso"); }}>Novo curso</Botao></div>}
      {!cursos.length && <Vazio titulo="Nenhum curso">Crie o primeiro curso, depois os módulos e as aulas. Nada aparece pro cliente antes de publicar.</Vazio>}
      {cursos.map((c) => (
        <Secao
          key={c.id}
          rotulo="Curso"
          titulo={c.titulo}
          acoes={admin ? <>
            <Selo tom={c.publicado ? "sucesso" : "neutro"}>{c.publicado ? "Publicado" : "Rascunho"}</Selo>
            <Botao tamanho="pequeno" variante="secundario" onClick={() => setEditando({ ...vazia, tipo: "curso", id: c.id, titulo: c.titulo, ordem: String(c.ordem) })}>Editar</Botao>
            <Botao tamanho="pequeno" variante="secundario" onClick={() => void publicar("cursos", c.id, !c.publicado)}>{c.publicado ? "Despublicar" : "Publicar"}</Botao>
            <Botao tamanho="pequeno" onClick={() => { setFeito(undefined); setNovo({ modulo: c.id }); }}>Novo módulo</Botao>
            <Botao tamanho="pequeno" variante="fantasma" aria-label={`Excluir o curso ${c.titulo}`} onClick={() => { setFeito(undefined); setExcluindo({ tipo: "cursos", id: c.id, titulo: c.titulo }); }}>Excluir</Botao>
          </> : <Selo tom={c.publicado ? "sucesso" : "neutro"}>{c.publicado ? "Publicado" : "Rascunho"}</Selo>}
        >
          {!c.modulos.length && <span className="mtc-texto-2">Sem módulos ainda.</span>}
          {c.modulos.map((m) => (
            <Pilha espaco={3} key={m.id}>
              <div className="mtc-linha mtc-linha--entre" style={{ gap: "var(--e-3)", alignItems: "flex-start" }}>
                <div style={{ display: "grid", gap: 2, minWidth: 0 }}>
                  <strong>{m.titulo}</strong>
                  <span className="mtc-texto-2" style={{ fontSize: "var(--t-xs)" }}>{m.planos.length ? `Abre com: ${m.planos.map(nomeDoPlano).join(", ")}` : "Abre pra qualquer plano ativo"}</span>
                </div>
                <div className="mtc-linha" style={{ gap: "var(--e-2)", flexWrap: "wrap" }}>
                  <Selo tom={m.publicado ? "sucesso" : "neutro"}>{m.publicado ? "Publicado" : "Rascunho"}</Selo>
                  {admin && <>
                    <Botao tamanho="pequeno" variante="secundario" onClick={() => setEditando({ ...vazia, tipo: "modulo", id: m.id, titulo: m.titulo, ordem: String(m.ordem), planos: m.planos })}>Editar</Botao>
                    <Botao tamanho="pequeno" variante="secundario" onClick={() => void publicar("modulos", m.id, !m.publicado)}>{m.publicado ? "Despublicar" : "Publicar"}</Botao>
                    <Botao tamanho="pequeno" onClick={() => { setFeito(undefined); setBiblioteca({ id: m.id, titulo: m.titulo }); }}>Trazer da biblioteca</Botao>
                    <Botao tamanho="pequeno" variante="secundario" onClick={() => { setFeito(undefined); setNovo({ aula: m.id }); }}>Nova aula</Botao>
                    <Botao tamanho="pequeno" variante="fantasma" aria-label={`Excluir o módulo ${m.titulo}`} onClick={() => { setFeito(undefined); setExcluindo({ tipo: "modulos", id: m.id, titulo: m.titulo }); }}>Excluir</Botao>
                  </>}
                </div>
              </div>
              <Tabela
                linhas={m.aulas}
                chave={(a) => a.id}
                paginacao={{ pagina: paginaDasAulas[m.id] ?? 1, aoMudar: (p) => setPaginaDasAulas((x) => ({ ...x, [m.id]: p })), rotulo: `Páginas das aulas de ${m.titulo}` }}
                vazio={<Vazio titulo="Sem aulas">Traga os vídeos da biblioteca ou crie uma aula.</Vazio>}
                colunas={[
                  { chave: "aula", rotulo: "Aula", celula: (a) => <><strong>{a.titulo}</strong><span className="mtc-tabela__apoio mtc-mono">{a.video_id ? `vídeo ${a.video_id}` : "sem vídeo"}{a.duracao_segundos ? ` · ${relogio(a.duracao_segundos)}` : ""}</span></> },
                  { chave: "situacao", rotulo: "Situação", celula: (a) => <Selo tom={a.publicada ? "sucesso" : "neutro"}>{a.publicada ? "Publicada" : "Rascunho"}</Selo> },
                  { chave: "assistida", rotulo: "Assistiram", alinhar: "direita", celula: (a) => <span className="mtc-mono">{a.assistida_por}</span> },
                  // Quem concluiu pelo botao conta, mas fica dito (035, 6.3): viu ou declarou que viu.
                  { chave: "concluida", rotulo: "Concluíram", alinhar: "direita", celula: (a) => <><span className="mtc-mono">{a.concluida_por}</span>{a.marcada_por > 0 && <span className="mtc-tabela__apoio">{a.marcada_por} pelo botão</span>}</> },
                  { chave: "perguntas", rotulo: "Perguntas", alinhar: "direita", celula: (a) => (a.perguntas_abertas ? <Selo tom="info">{a.perguntas_abertas}</Selo> : <span className="mtc-texto-2">0</span>) },
                  ...(admin ? [{ chave: "acoes", rotulo: "", celula: (a: Aula) => <div className="mtc-linha" style={{ gap: "var(--e-2)" }}><Botao tamanho="pequeno" variante="secundario" onClick={() => setEditando({ ...vazia, tipo: "aula", id: a.id, titulo: a.titulo, ordem: String(a.ordem), video: a.video_id ?? "", duracao: a.duracao_segundos ? String(a.duracao_segundos) : "", texto: a.texto ?? "", modulo: m.id })}>Editar</Botao><Botao tamanho="pequeno" variante="secundario" onClick={() => void publicar("aulas", a.id, !a.publicada)}>{a.publicada ? "Despublicar" : "Publicar"}</Botao><Botao tamanho="pequeno" variante="fantasma" aria-label={`Excluir a aula ${a.titulo}`} onClick={() => { setFeito(undefined); setExcluindo({ tipo: "aulas", id: a.id, titulo: a.titulo }); }}>Excluir</Botao></div> }] : []),
                ]}
              />
            </Pilha>
          ))}
        </Secao>
      ))}
      <NovoItem alvo={novo} aoFechar={() => setNovo(null)} aoCriar={(t) => { void recarregar(); setFeito(`${t} criado. Publique quando estiver pronto.`); }} />
      <Editar item={editando} modulos={modulos} aoFechar={() => setEditando(null)} aoSalvar={() => { void recarregar(); setFeito("Alterado."); }} />
      <TrazerDaBiblioteca modulo={biblioteca} aoFechar={() => setBiblioteca(null)} aoTrazer={(frase) => { void recarregar(); setFeito(frase); }} />
      <Excluir alvo={excluindo} aoFechar={() => setExcluindo(null)} aoExcluir={(frase) => { void recarregar(); void qc.invalidateQueries({ queryKey: ["academy-perguntas"] }); setFeito(frase); }} />
    </Pilha>
  );
}

// ---------------------------------------------------------------- excluir (Vitor, 20/10)

type AlvoDaExclusao = { tipo: "cursos" | "modulos" | "aulas"; id: string; titulo: string };
type Impacto = { titulo: string; publicado: boolean; modulos: number; aulas: number; pessoas_com_progresso: number; perguntas: number; perguntas_abertas: number; videos: number };
const NOME: Record<AlvoDaExclusao["tipo"], { artigo: string; nome: string }> = { cursos: { artigo: "o", nome: "curso" }, modulos: { artigo: "o", nome: "módulo" }, aulas: { artigo: "a", nome: "aula" } };
const contar = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

/**
 * Excluir curso, modulo ou aula, com tudo dentro. Antes de confirmar, diz o que vai junto (Vitor, 20/10: "com aviso se
 * tiver aulas dentro que vai tirar as aulas dentro daquele curso"), quem perde o progresso e se some da area na hora.
 */
function Excluir({ alvo, aoFechar, aoExcluir }: { alvo: AlvoDaExclusao | null; aoFechar: () => void; aoExcluir: (frase: string) => void }) {
  const { ler, apagar } = useApi();
  const impacto = useQuery({ queryKey: ["academy-exclusao", alvo?.tipo, alvo?.id], queryFn: () => ler<Impacto>(`/v1/admin/academy/${alvo!.tipo}/${alvo!.id}/exclusao`), enabled: Boolean(alvo), gcTime: 0 });
  const n = alvo ? NOME[alvo.tipo] : NOME.cursos;
  const i = impacto.data;
  const dentro = !i ? null : alvo?.tipo === "cursos"
    ? (i.modulos || i.aulas ? `Vão junto ${contar(i.modulos, "módulo", "módulos")} e ${contar(i.aulas, "aula", "aulas")}.` : "O curso está vazio: não tem módulo nem aula.")
    : alvo?.tipo === "modulos" ? (i.aulas ? `Vão junto ${contar(i.aulas, "aula", "aulas")}.` : "O módulo está vazio: não tem aula.") : null;
  return (
    <DialogoDeAcao
      aberto={Boolean(alvo)}
      aoFechar={aoFechar}
      titulo={alvo ? `Excluir ${n.artigo} ${n.nome} ${alvo.titulo}?` : ""}
      botao={`Excluir ${n.nome}`}
      variante="perigo"
      podeEnviar={Boolean(i)}
      aoEnviar={() => apagar(`/v1/admin/academy/${alvo!.tipo}/${alvo!.id}`)}
      aoConcluir={() => aoExcluir(`${n.nome.charAt(0).toUpperCase()}${n.nome.slice(1)} ${alvo!.titulo} ${n.artigo === "a" ? "excluída" : "excluído"}.`)}
    >
      {impacto.isLoading ? <Carregando compacto texto="Vendo o que vai junto" /> : impacto.isError ? <Aviso tipo="erro">{erroDe(impacto.error)}</Aviso> : i && (
        <Aviso tipo={i.aulas || i.publicado ? "erro" : "alerta"}>
          <Pilha espaco={2}>
            {dentro && <strong>{dentro}</strong>}
            {i.pessoas_com_progresso > 0 && <span>{contar(i.pessoas_com_progresso, "cliente já assistiu", "clientes já assistiram")}: o progresso nessas aulas some.</span>}
            {i.perguntas > 0 && <span>{contar(i.perguntas, "pergunta some", "perguntas somem")} junto{i.perguntas_abertas ? `, ${contar(i.perguntas_abertas, "sem resposta", "sem resposta")} (sai do sino)` : ""}.</span>}
            {i.publicado && <span>Está publicad{n.artigo === "a" ? "a" : "o"}: some da área dos clientes na hora.</span>}
            {i.videos > 0 && <span>Os vídeos continuam no Cloudflare e voltam pra biblioteca: dá pra trazer de novo.</span>}
            <span>Não dá pra desfazer.</span>
          </Pilha>
        </Aviso>
      )}
    </DialogoDeAcao>
  );
}

function NovoItem({ alvo, aoFechar, aoCriar }: { alvo: "curso" | { modulo: string } | { aula: string } | null; aoFechar: () => void; aoCriar: (tipo: string) => void }) {
  const { postar } = useApi();
  const planos = usePlanos();
  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [video, setVideo] = useState("");
  const [duracao, setDuracao] = useState("");
  const [plano, setPlano] = useState("");
  const tipo = alvo === "curso" ? "Curso" : alvo && "modulo" in alvo ? "Módulo" : "Aula";
  const limpar = () => { setTitulo(""); setDescricao(""); setVideo(""); setDuracao(""); setPlano(""); };
  return (
    <DialogoDeAcao
      aberto={Boolean(alvo)}
      aoFechar={aoFechar}
      titulo={`${tipo === "Curso" ? "Novo curso" : tipo === "Módulo" ? "Novo módulo" : "Nova aula"}`}
      descricao="Nasce como rascunho: o cliente só vê depois de publicar."
      botao="Criar"
      podeEnviar={titulo.trim().length >= 2}
      aoAbrir={limpar}
      aoEnviar={() => {
        const base = { slug: slugDe(titulo), titulo: titulo.trim(), descricao: descricao.trim() || null };
        if (alvo === "curso") return postar("/v1/admin/academy/cursos", base);
        if (alvo && "modulo" in alvo) return postar("/v1/admin/academy/modulos", { ...base, curso_id: alvo.modulo, planos: plano ? [plano] : [] });
        return postar("/v1/admin/academy/aulas", { ...base, modulo_id: (alvo as { aula: string }).aula, video_id: video.trim() || null, duracao_segundos: duracao.trim() ? Number(duracao) : null });
      }}
      aoConcluir={() => aoCriar(tipo)}
    >
      <Campo rotulo="Título" valor={titulo} aoMudar={setTitulo} required dica={titulo.trim() ? `Endereço: ${slugDe(titulo)}` : undefined} />
      <CampoTextoLongo rotulo="Descrição (opcional)" valor={descricao} aoMudar={setDescricao} maxLength={1000} />
      {alvo && typeof alvo !== "string" && "modulo" in alvo && (
        <CampoSelecao rotulo="Abre com o plano" valor={plano} aoMudar={setPlano} vazio="Qualquer plano ativo" opcoes={(planos.data?.itens ?? []).map((p) => ({ valor: p.slug, rotulo: p.nome }))} dica="Deixe em branco pra liberar pra quem tem qualquer plano ativo." />
      )}
      {alvo && typeof alvo !== "string" && "aula" in alvo && (
        <>
          <Campo rotulo="Id do vídeo no Cloudflare Stream (opcional)" valor={video} aoMudar={setVideo} mono />
          <Campo rotulo="Duração em segundos (opcional)" valor={duracao} aoMudar={(v) => setDuracao(v.replace(/\D/g, ""))} mono dica="Em branco, vem do próprio vídeo. Serve pra calcular o progresso do cliente." />
        </>
      )}
    </DialogoDeAcao>
  );
}

function Editar({ item, modulos, aoFechar, aoSalvar }: { item: Edicao | null; modulos: { valor: string; rotulo: string }[]; aoFechar: () => void; aoSalvar: () => void }) {
  const { por } = useApi();
  const planos = usePlanos();
  const [f, setF] = useState({ titulo: "", ordem: "0", plano: "", video: "", duracao: "", texto: "", modulo: "" });
  const rota = item?.tipo === "curso" ? "cursos" : item?.tipo === "modulo" ? "modulos" : "aulas";
  return (
    <DialogoDeAcao
      aberto={Boolean(item)}
      aoFechar={aoFechar}
      titulo={`Editar ${item?.tipo ?? ""}`}
      botao="Salvar"
      podeEnviar={f.titulo.trim().length >= 2}
      aoAbrir={() => item && setF({ titulo: item.titulo, ordem: item.ordem, plano: item.planos[0] ?? "", video: item.video, duracao: item.duracao, texto: item.texto, modulo: item.modulo })}
      aoEnviar={() =>
        por(`/v1/admin/academy/${rota}/${item!.id}`, {
          titulo: f.titulo.trim(),
          ordem: Number(f.ordem) || 0,
          ...(item!.tipo === "modulo" ? { planos: f.plano ? [f.plano] : [] } : {}),
          ...(item!.tipo === "aula" ? { video_id: f.video.trim() || null, duracao_segundos: f.duracao.trim() ? Number(f.duracao) : null, texto: f.texto.trim() || null, ...(f.modulo && f.modulo !== item!.modulo ? { modulo_id: f.modulo } : {}) } : {}),
        })
      }
      aoConcluir={aoSalvar}
    >
      <Campo rotulo="Título" valor={f.titulo} aoMudar={(v) => setF({ ...f, titulo: v })} />
      <Campo rotulo="Ordem" valor={f.ordem} aoMudar={(v) => setF({ ...f, ordem: v.replace(/\D/g, "") })} mono dica="Menor aparece antes." />
      {item?.tipo === "modulo" && <CampoSelecao rotulo="Abre com o plano" valor={f.plano} aoMudar={(v) => setF({ ...f, plano: v })} vazio="Qualquer plano ativo" opcoes={(planos.data?.itens ?? []).map((p) => ({ valor: p.slug, rotulo: p.nome }))} />}
      {item?.tipo === "aula" && (
        <>
          {modulos.length > 1 && <CampoSelecao rotulo="Módulo" valor={f.modulo} aoMudar={(v) => setF({ ...f, modulo: v })} opcoes={modulos} dica="A aula leva junto o progresso e as dúvidas dos clientes." />}
          <Campo rotulo="Id do vídeo no Cloudflare Stream" valor={f.video} aoMudar={(v) => setF({ ...f, video: v })} mono />
          <Campo rotulo="Duração em segundos" valor={f.duracao} aoMudar={(v) => setF({ ...f, duracao: v.replace(/\D/g, "") })} mono />
          <CampoTextoLongo rotulo="Texto de apoio" valor={f.texto} aoMudar={(v) => setF({ ...f, texto: v })} maxLength={20_000} linhas={6} />
        </>
      )}
    </DialogoDeAcao>
  );
}

/**
 * A biblioteca do Cloudflare Stream (035, 6.5): o administrador marca os videos e cada um vira uma aula neste modulo,
 * com titulo e duracao do proprio video, protegida por URL assinada e em rascunho. Video que ja e aula aparece, mas
 * nao da pra marcar: um video, uma aula.
 */
function TrazerDaBiblioteca({ modulo, aoFechar, aoTrazer }: { modulo: { id: string; titulo: string } | null; aoFechar: () => void; aoTrazer: (frase: string) => void }) {
  const { ler, postar } = useApi();
  const [marcados, setMarcados] = useState<string[]>([]);
  const q = useQuery({ queryKey: ["academy-biblioteca"], queryFn: () => ler<{ videos: VideoDaBiblioteca[] }>("/v1/admin/academy/biblioteca"), enabled: Boolean(modulo), staleTime: 0, retry: false });
  const videos = q.data?.videos ?? [];
  const livres = videos.filter((v) => v.pronto && !v.aula);
  const alternar = (id: string, sim: boolean) => setMarcados((m) => (sim ? [...m, id] : m.filter((x) => x !== id)));
  return (
    <DialogoDeAcao
      aberto={Boolean(modulo)}
      aoFechar={aoFechar}
      titulo="Trazer da biblioteca"
      descricao={modulo ? `Cada vídeo marcado vira uma aula em "${modulo.titulo}", em rascunho, com o título e a duração do vídeo.` : undefined}
      botao={marcados.length > 1 ? `Criar ${marcados.length} aulas` : "Criar aula"}
      podeEnviar={marcados.length > 0}
      aoAbrir={() => setMarcados([])}
      aoEnviar={async () => {
        const r = await postar<{ criadas: { id: string; titulo: string }[]; ja_eram_aula: number; nao_prontos: number }>("/v1/admin/academy/biblioteca/trazer", { modulo_id: modulo!.id, video_ids: marcados });
        const extras = [r.ja_eram_aula ? `${r.ja_eram_aula} já era aula` : "", r.nao_prontos ? `${r.nao_prontos} ainda processando` : ""].filter(Boolean).join(", ");
        aoTrazer(`${r.criadas.length === 1 ? "1 aula criada" : `${r.criadas.length} aulas criadas`} em rascunho${extras ? ` (${extras})` : ""}. Confira e publique.`);
      }}
      aoConcluir={aoFechar}
    >
      {q.isLoading ? <Carregando compacto texto="Lendo a biblioteca" /> : q.isError ? <Aviso tipo="erro">{erroDe(q.error)}</Aviso> : !videos.length ? (
        <Vazio titulo="Biblioteca vazia">Suba o vídeo no Cloudflare Stream e volte aqui.</Vazio>
      ) : (
        <Pilha espaco={2}>
          {!livres.length && <Aviso tipo="info">Todos os vídeos prontos da biblioteca já são aulas.</Aviso>}
          {videos.map((v) => (
            <CampoCaixa key={v.video_id} marcado={marcados.includes(v.video_id)} aoMudar={(sim) => alternar(v.video_id, sim)} disabled={!v.pronto || Boolean(v.aula)}>
              <strong>{v.titulo}</strong>
              <span className="mtc-texto-2" style={{ fontSize: "var(--t-xs)", display: "block" }}>
                {[v.duracao_segundos ? relogio(v.duracao_segundos) : null, !v.pronto ? "processando" : null, v.aula ? `já é a aula "${v.aula.titulo}" (${v.aula.modulo})` : null, v.pronto && !v.protegido && !v.aula ? "vai ser protegido ao criar" : null].filter(Boolean).join(" · ")}
              </span>
            </CampoCaixa>
          ))}
        </Pilha>
      )}
    </DialogoDeAcao>
  );
}

// ---------------------------------------------------------------- perguntas

function Perguntas() {
  const { ler, postar } = useApi();
  const { pode } = usePapel();
  const qc = useQueryClient();
  const [estado, setEstado] = useState<"abertas" | "todas">("abertas");
  const [responder, setResponder] = useState<Pergunta | null>(null);
  const [resolver, setResolver] = useState<Pergunta | null>(null);
  const [texto, setTexto] = useState("");
  const [feito, setFeito] = useState<string>();
  const recarregar = () => { void qc.invalidateQueries({ queryKey: ["academy-perguntas"] }); void qc.invalidateQueries({ queryKey: ["academy-catalogo"] }); void qc.invalidateQueries({ queryKey: ["avisos"] }); };
  const responde = pode("SUPORTE");
  const q = useQuery({ queryKey: ["academy-perguntas", estado], queryFn: () => ler<{ itens: Pergunta[] }>(`/v1/admin/academy/perguntas?estado=${estado}`) });
  return (
    <Pilha espaco={4}>
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      <div style={{ maxWidth: 260 }}>
        <CampoSelecao rotulo="Situação" valor={estado} aoMudar={(v) => setEstado(v as "abertas" | "todas")} opcoes={[{ valor: "abertas", rotulo: "Esperando resposta" }, { valor: "todas", rotulo: "Todas" }]} />
      </div>
      {q.isLoading ? <Carregando compacto texto="Buscando as perguntas" /> : q.isError ? <Aviso tipo="erro">{erroDe(q.error)}</Aviso> : (
        <Tabela
          linhas={q.data?.itens ?? []}
          chave={(p) => p.id}
          vazio={<Vazio titulo="Nenhuma pergunta">{estado === "abertas" ? "Ninguém está esperando resposta." : "Nenhuma pergunta ainda."}</Vazio>}
          colunas={[
            { chave: "quem", rotulo: "Cliente", celula: (p) => <>{p.pessoa.nome}<span className="mtc-tabela__apoio">{formatarDataHora(p.criada_em)}</span></> },
            { chave: "aula", rotulo: "Aula", celula: (p) => <>{p.aula.titulo}<span className="mtc-tabela__apoio">{p.aula.curso} · {p.aula.modulo} · aos {relogio(p.segundo)}</span></> },
            { chave: "pergunta", rotulo: "Pergunta", celula: (p) => p.texto },
            {
              chave: "respostas",
              rotulo: "Conversa",
              celula: (p) => (
                <div style={{ display: "grid", gap: "var(--e-2)" }}>
                  {p.respostas.map((r) => <div key={r.id}>{r.texto}<span className="mtc-tabela__apoio">{r.autor}, {formatarDataHora(r.criada_em)}</span></div>)}
                  {!p.respostas.length && <span className="mtc-texto-2">Ninguém respondeu ainda.</span>}
                </div>
              ),
            },
            {
              chave: "situacao",
              rotulo: "Situação",
              celula: (p) => (
                <div style={{ display: "grid", gap: "var(--e-1)", justifyItems: "start", whiteSpace: "nowrap" }}>
                  {p.resolvida_em ? <Selo tom="sucesso">{p.resolvida_por === "cliente" ? "Resolvida pelo cliente" : "Resolvida"}</Selo> : p.respondida_em ? <Selo tom="info">Respondida</Selo> : <Selo tom="alerta">Esperando</Selo>}
                  {responde && <button type="button" className="mtc-link" onClick={() => { setTexto(""); setResponder(p); }}>{p.respostas.length ? "Responder de novo" : "Responder"}</button>}
                  {responde && !p.resolvida_em && <button type="button" className="mtc-link" onClick={() => setResolver(p)}>Marcar como resolvida</button>}
                </div>
              ),
            },
          ]}
        />
      )}
      <DialogoDeAcao
        aberto={Boolean(responder)}
        aoFechar={() => setResponder(null)}
        titulo="Responder a pergunta"
        descricao={responder ? `${responder.pessoa.nome}, em ${responder.aula.titulo}: "${responder.texto}"` : undefined}
        botao="Responder"
        podeEnviar={texto.trim().length >= 2}
        aoEnviar={() => postar(`/v1/admin/academy/perguntas/${responder!.id}/responder`, { texto: texto.trim() })}
        aoConcluir={() => { setFeito(responder?.respostas.length ? "Resposta enviada. O cliente recebe o aviso." : "Respondida. O cliente recebe o aviso e a pendência sai do sino."); setResponder(null); recarregar(); }}
      >
        {responder?.respostas.map((r) => <Aviso key={r.id} tipo="info">{r.autor}: {r.texto}</Aviso>)}
        <CampoTextoLongo rotulo="Resposta" valor={texto} aoMudar={setTexto} maxLength={4000} linhas={5} dica="O cliente lê na própria aula e recebe um aviso." />
      </DialogoDeAcao>
      <DialogoDeAcao
        aberto={Boolean(resolver)}
        aoFechar={() => setResolver(null)}
        titulo="Marcar como resolvida"
        descricao={resolver ? `${resolver.pessoa.nome}, em ${resolver.aula.titulo}: "${resolver.texto}". Sai da fila e o cliente vê a dúvida como resolvida.` : undefined}
        botao="Marcar como resolvida"
        aoEnviar={() => postar(`/v1/admin/academy/perguntas/${resolver!.id}/resolver`, {})}
        aoConcluir={() => { setFeito("Marcada como resolvida."); setResolver(null); recarregar(); }}
      />
    </Pilha>
  );
}
